type SizeUnit = 'B' | 'KB' | 'MB' | 'GB' | 'TB' | 'PB';

interface SizeDisplayProps {
    bytes: number;
    /**
     * 小数点以下の表示桁数（デフォルト：2）
     */
    decimalPlaces?: number;
    /**
     * 最大表示単位（例: 'GB' までに制限）
     */
    maxUnit?: SizeUnit;
    className?: string;
}

/**
 * バイト数を人間が読みやすい形式に変換
 * 例: 1536 → "1.50 KB", 2147483648 → "2.00 GB"
 */
const formatBytes = (
    bytes: number,
    decimalPlaces: number = 2
): { value: string; unit: SizeUnit } => {
    if (!Number.isFinite(bytes) || bytes < 0) return { value: '0', unit: 'B' };

    const units: SizeUnit[] = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    let unitIndex = 0;

    // 単位を上位へ進める（1024で割る）
    while (bytes >= 1024 && unitIndex < units.length - 1) {
        bytes /= 1024;
        unitIndex++;
    }

    // 小数点以下を指定桁数に丸め、文字列化
    const value = Number.isInteger(bytes)
        ? bytes.toString()
        : bytes.toFixed(decimalPlaces);

    return { value, unit: units[unitIndex] };
};

export const SizeDisplay: React.FC<SizeDisplayProps> = ({
    bytes,
    decimalPlaces = 2,
    maxUnit = 'PB',
    className = '',
}) => {
    const { value, unit } = formatBytes(bytes, decimalPlaces);

    // 最大単位を制限（例：GB以上にはしない）
    if (unit !== 'B') {
        const maxIndex = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'].indexOf(maxUnit);
        const currentIndex = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'].indexOf(unit);

        if (currentIndex > maxIndex) {
            // 最大単位で再計算（maxUnit の次より下まで戻す）
            let adjustedBytes = bytes;
            for (let i = 0; i < currentIndex - maxIndex; i++) {
                adjustedBytes /= 1024;
            }
            const adjustedValue = Number.isInteger(adjustedBytes)
                ? Math.round(adjustedBytes).toString()
                : adjustedBytes.toFixed(decimalPlaces);

            return (
                <span className={`size-display ${className}`}>
                    {adjustedValue} <span className="unit">{maxUnit}</span>
                </span>
            );
        }
    }

    return (
        <span
            className={`size-display ${className}`}
            title={`${bytes.toLocaleString()} バイト`}
        >
            {value} <span className="unit">{unit}</span>
        </span>
    );
};
